import {DataStatus, Switch, Choice, Card, Separator} from 'platform/components';
import {Box, HStack, Heading, Show, Space, Text} from 'platform/foundation';

import {useState} from 'react';

import {isNotNil} from 'ramda';

import {
  useGetServiceOrderVehicleClassQuery,
  useGetVehicleClassesQuery,
  usePostServiceOrderAssignVehicleClassMutation,
  usePostServiceOrderUnassignVehicleClassMutation,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {ApiError, TestIdProps, suffixTestId} from 'shared/web';

interface VehicleClassSectionProps extends TestIdProps {
  serviceCaseId: string;
  serviceOrderId: string;
  isReadOnly?: boolean;
}

export function VehicleClassSection(props: VehicleClassSectionProps) {
  const [selectedVehicleClassId, setSelectedVehicleClassId] = useState<string>();
  const [isAssignEnabled, setIsAssignEnabled] = useState(false);

  const {data, isLoading, isError} = useGetServiceOrderVehicleClassQuery({
    serviceCaseId: props.serviceCaseId,
    serviceOrderId: props.serviceOrderId,
  });

  const {
    data: vehicleClasses,
    isLoading: isVehicleClassesLoading,
    isError: isVehicleClassesError,
  } = useGetVehicleClassesQuery();

  const [assignVehicleClass, {isLoading: isAssigning}] =
    usePostServiceOrderAssignVehicleClassMutation();
  const [unassignVehicleClass, {isLoading: isUnassigning}] =
    usePostServiceOrderUnassignVehicleClassMutation();

  const handleToggleAssign = (enabled: boolean) => {
    setIsAssignEnabled(enabled);
    if (!enabled) {
      unassignVehicleClass({
        serviceCaseId: props.serviceCaseId,
        serviceOrderId: props.serviceOrderId,
      })
        .unwrap()
        .then(() => {
          setSelectedVehicleClassId(undefined);
        })
        .catch((e: ApiError) => {
          handleApiError(e);

          setIsAssignEnabled(true);
        });
    }
  };

  const handleVehicleClassChange = (vehicleClassId?: string | null) => {
    setSelectedVehicleClassId(vehicleClassId ?? undefined);
    if ((isAssignEnabled || isNotNil(data?.vehicleClassId)) && vehicleClassId) {
      assignVehicleClass({
        serviceCaseId: props.serviceCaseId,
        serviceOrderId: props.serviceOrderId,
        vehicleClassId,
      })
        .unwrap()
        .catch((e: ApiError) => {
          handleApiError(e);

          setSelectedVehicleClassId(data?.vehicleClassId);
        });
    }
  };

  const currentVehicleClassId = data?.vehicleClassId ?? selectedVehicleClassId;

  const vehicleClassOptions =
    vehicleClasses
      ?.filter((vehicleClass) => vehicleClass.isActive)
      .map((vehicleClass) => ({
        value: vehicleClass.vehicleClassId,
        label: vehicleClass.name,
      })) ?? [];

  return (
    <DataStatus
      isLoading={isLoading || isVehicleClassesLoading}
      isError={isError || isVehicleClassesError}
      minHeight={24}
    >
      <Heading size={4}>{i18n.t('entity.vehicleClass.labels.vehicleClass')}</Heading>
      <Space vertical={1} />
      <Text size="small" color="secondary">
        {i18n.t('entity.order.labels.vehicleClassDescription')}
      </Text>
      <Space vertical={4} />
      <Card variant="inlineGrey">
        <HStack align="center" spacing={3}>
          <Switch
            label={i18n.t('entity.order.actions.assignVehicleClass')}
            value={isNotNil(data?.vehicleClassId) || isAssignEnabled}
            onChange={handleToggleAssign}
            isDisabled={props.isReadOnly || isAssigning || isUnassigning}
            data-testid={suffixTestId('assignVehicleClassSwitch', props)}
          />
        </HStack>
        <Show when={isNotNil(data?.vehicleClassId) || isAssignEnabled}>
          <Separator />
          <Box width={65}>
            <Choice
              label={i18n.t('entity.vehicleClass.labels.vehicleClass')}
              value={currentVehicleClassId ?? null}
              onChange={handleVehicleClassChange}
              options={vehicleClassOptions}
              isDisabled={props.isReadOnly || isAssigning || isUnassigning}
              isLoading={isAssigning}
              isNotClearable
              data-testid={suffixTestId('vehicleClassSelect', props)}
            />
          </Box>
        </Show>
      </Card>
    </DataStatus>
  );
}
